import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ArrowDown2, DocumentText1, Edit2, Import, Trash } from "iconsax-react";
import cn from "classnames";
import { Button, Dropdown, DropdownItem, PromptForm } from "@/shared/ui";
import { ORDER_ACKNOWLEDGED } from "@/features/hr/contracts/consts";
import type { CombinedContractListItem } from "@/features/hr/contracts/types";
import { type ContractAction, getAvailableActions } from "@/features/hr/contracts/hooks";
import { filterActionsByPermissions, getCompleteHiringPromptContent, getIdFromApiUrl } from "@/features/hr/contracts/utils";
import { useAuth } from "@/shared/hooks";
import { RevokeSigningButton } from "@/features/signing";

const ACTION_ICON_MAP: Partial<Record<ContractAction, React.ReactNode>> = {
  submit_for_signing: <DocumentText1 size={16} color="currentColor" />,
  download_contract_pdf: <Import size={16} color="currentColor" />,
  download_contract_docx: <Import size={16} color="currentColor" />,
  complete_hiring: <DocumentText1 size={16} color="currentColor" />,
  edit_contract: <Edit2 size={16} color="currentColor" />,
  delete_contract: <Trash size={16} color="currentColor" />,
};

interface Props {
  contract: CombinedContractListItem;
  isLoading: {
    isDownloading: boolean;
    isSubmitting: boolean;
    isPreviewing: boolean;
  };
  downloadingContractId: number | null;
  previewingContractId: number | null;
  submittingContractId: number | null;
  previewingJobApplicationCandidateId: number | null;
  reviewingJobApplicationCandidateId: number | null;
  previewingOrderCandidateId: number | null;
  completingHiringCandidateId: number | null;
  onAction: (action: ContractAction, contractId: number, contract?: CombinedContractListItem) => Promise<void>;
  className?: string;
}

export default function ContractActionsButtons({
  contract,
  isLoading,
  downloadingContractId,
  previewingContractId,
  submittingContractId,
  previewingJobApplicationCandidateId,
  reviewingJobApplicationCandidateId,
  previewingOrderCandidateId,
  completingHiringCandidateId,
  onAction,
  className,
}: Props) {
  const { t } = useTranslation("ContractsPage");
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [showConfirmCompleteHiring, setShowConfirmCompleteHiring] = useState(false);
  const [showConfirmDeleteContract, setShowConfirmDeleteContract] = useState(false);

  const isThisContractDownloading = downloadingContractId === contract.id;
  const isThisContractPreviewing = previewingContractId === contract.id;
  const isThisContractSubmitting = submittingContractId === contract.id;
  const isThisCandidatePreviewing = previewingJobApplicationCandidateId === contract.candidate_application_id;
  const isThisCandidateReviewing = reviewingJobApplicationCandidateId === contract.candidate_application_id;
  const orderId = getIdFromApiUrl(contract.order_url);
  const isThisOrderPreviewing = orderId !== null ? previewingOrderCandidateId === orderId : false;
  const isThisOrderCompleting = orderId !== null ? completingHiringCandidateId === orderId : false;

  const { actions: allActions } = getAvailableActions(
    contract,
    isLoading.isDownloading ||
      isLoading.isSubmitting ||
      isThisContractPreviewing ||
      isThisContractSubmitting ||
      isThisCandidatePreviewing ||
      isThisCandidateReviewing ||
      isThisOrderPreviewing ||
      isThisOrderCompleting,
    t,
  );

  const actions = filterActionsByPermissions(allActions, hasPermission);

  const compactActionIds = new Set<ContractAction>(["edit_contract", "delete_contract"]);
  const hasMainActions = actions.some((a) => !compactActionIds.has(a.id));
  const compactActions = hasMainActions ? actions.filter((a) => compactActionIds.has(a.id)) : [];
  const mainActions = hasMainActions ? actions.filter((a) => !compactActionIds.has(a.id)) : actions;

  const completeHiringPromptContent = getCompleteHiringPromptContent(
    contract.candidate_stage === ORDER_ACKNOWLEDGED,
    t,
  );

  function getActionLoadingState(actionId: ContractAction): { isDisabled: boolean; loadingText: string } {
    const loadingMap: Partial<Record<ContractAction, boolean>> = {
      download: isThisContractDownloading,
      submit_for_signing: isThisContractSubmitting,
      preview: isThisContractPreviewing,
      preview_job_application: isThisCandidatePreviewing,
      review_job_application: isThisCandidateReviewing,
      review_job_application_revision: isThisCandidateReviewing,
      review_job_application_reject: isThisCandidateReviewing,
      preview_order: isThisOrderPreviewing,
      complete_hiring: isThisOrderCompleting,
      download_contract_pdf: isThisContractDownloading,
      download_contract_docx: isThisContractPreviewing,
    };

    const textMap: Partial<Record<ContractAction, string>> = {
      submit_for_signing: t("actions.submitting"),
      review_job_application: t("jobApplication.actions.reviewing"),
      review_job_application_revision: t("jobApplication.actions.reviewing"),
      review_job_application_reject: t("jobApplication.actions.reviewing"),
      complete_hiring: t("order.actions.completing"),
    };

    const isDisabled = loadingMap[actionId] ?? false;
    return { isDisabled, loadingText: isDisabled ? (textMap[actionId] ?? t("actions.downloading")) : "" };
  }

  return (
    <>
      {compactActions.length > 0 && (
        <div className="absolute top-4 right-4 grid grid-cols-2 gap-2">
          {compactActions.map((action) => (
            <Button
              key={action.id}
              variant={action.id === "delete_contract" ? "danger" : "secondary"}
              size="md"
              className="w-8! h-8! p-0! rounded-md!"
              isIconButton
              onClick={(e) => {
                e.stopPropagation();
                if (action.id === "edit_contract") {
                  navigate(`/hr/contracts/edit/${contract.id}`);
                } else if (action.id === "delete_contract") {
                  setShowConfirmDeleteContract(true);
                }
              }}>
              {ACTION_ICON_MAP[action.id]}
            </Button>
          ))}
        </div>
      )}
    <div className={cn("flex flex-col gap-2 pt-3 border-t surface-base-stroke", className)}>
      {mainActions.map((action) => {
        if (action.id === "download_contract") {
          return (
            <Dropdown
              key={action.id}
              open={isDownloadDropdownOpen}
              onClose={() => setIsDownloadDropdownOpen(false)}
              direction="bottom"
              fullWidth={true}>
              <Button
                variant={action.variant}
                size="md"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDownloadDropdownOpen(!isDownloadDropdownOpen);
                }}>
                <div className="flex justify-between items-center w-full relative">
                  <div></div>
                  <div className="flex items-center gap-2">
                    <Import size={16} color="currentColor" />
                    <span className="flex-1 text-center">{action.label}</span>
                  </div>
                  <ArrowDown2
                    size={16}
                    color="currentColor"
                    className={`transition-transform ${isDownloadDropdownOpen ? "rotate-180" : ""}`}
                  />
                </div>
              </Button>
              {contract.signing_document?.signed_pdf_url && (
                <DropdownItem
                  className="justify-center !text-center"
                  onClick={() => {
                    window.open(contract.signing_document!.signed_pdf_url!, "_blank");
                    setIsDownloadDropdownOpen(false);
                  }}>
                  {t("contract.downloadSignedPdf")}
                </DropdownItem>
              )}
              {contract.signing_document?.original_file_url && (
                <DropdownItem
                  className="justify-center !text-center"
                  onClick={() => {
                    window.open(contract.signing_document!.original_file_url!, "_blank");
                    setIsDownloadDropdownOpen(false);
                  }}>
                  {t("contract.downloadOriginal")}
                </DropdownItem>
              )}
            </Dropdown>
          );
        }

        const { isDisabled, loadingText } = getActionLoadingState(action.id);

        const finalDisabled = isDisabled || (action.id === "complete_hiring" && !contract.order_signed_pdf_url);

        return (
          <div key={action.id} className="flex flex-col gap-2">
            <Button
              variant={action.variant}
              size="md"
              disabled={finalDisabled}
              className="w-full"
              onClick={async (e) => {
                e.stopPropagation();
                if (action.id === "edit_contract") {
                  navigate(`/hr/contracts/edit/${contract.id}`);
                } else if (action.id === "delete_contract") {
                  setShowConfirmDeleteContract(true);
                } else if (action.id === "submit_for_signing") {
                  setShowConfirmSubmit(true);
                } else if (action.id === "complete_hiring") {
                  setShowConfirmCompleteHiring(true);
                } else {
                  await onAction(action.id, contract.id, contract);
                }
              }}>
              {ACTION_ICON_MAP[action.id]}
              {isDisabled && loadingText ? loadingText : action.label}
            </Button>
          </div>
        );
      })}

      {showConfirmSubmit && (
        <PromptForm
          variant="warning"
          onClose={() => setShowConfirmSubmit(false)}
          onConfirm={async () => {
            await onAction("submit_for_signing", contract.id, contract);
            setShowConfirmSubmit(false);
          }}
          title={t("prompt_form.title")}
          text={t("prompt_form.description")}
          isLoading={isThisContractSubmitting}
          namespace="ContractsPage"
        />
      )}

      {showConfirmCompleteHiring && (
        <PromptForm
          variant="warning"
          onClose={() => setShowConfirmCompleteHiring(false)}
          onConfirm={async () => {
            if (contract.candidate_stage === ORDER_ACKNOWLEDGED) {
              await onAction("complete_hiring", contract.id, contract);
            }
            setShowConfirmCompleteHiring(false);
          }}
          title={completeHiringPromptContent.title}
          text={completeHiringPromptContent.text}
          confirmText={completeHiringPromptContent.confirmText}
          isLoading={isThisOrderCompleting}
          namespace="ContractsPage"
        />
      )}

      {showConfirmDeleteContract && (
        <PromptForm
          variant="error"
          onClose={() => setShowConfirmDeleteContract(false)}
          onConfirm={async () => {
            await onAction("delete_contract", contract.id, contract);
            setShowConfirmDeleteContract(false);
          }}
          title={t("confirm.deleteContractTitle")}
          text={t("confirm.deleteContractText")}
          namespace="ContractsPage"
        />
      )}

      {contract.signing_document &&
        (contract.signing_document.status === 0 || contract.signing_document.status === 1) &&
        user?.iin === contract.signing_document.org_signer_iin && (
          <RevokeSigningButton
            documentId={contract.signing_document.document_id}
            onSuccess={() => {}}
            disabled={isLoading.isSubmitting || isLoading.isDownloading}
          />
        )}

    </div>
    </>
  );
}
